import { Injectable, OnDestroy } from "@angular/core";
import { Observable, throwError, Subscription } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { createSelector, select, Store } from "@ngrx/store";
import { AuthenticateResponse, AuthenticateToken, LoginByCertificate } from "@helper/abstraction/authenticate";
import { Role } from "@helper/abstraction/roles";
import { setPending } from "@app/app.actions";
import * as UserActions from "../../user.actions";
import { UserState } from "../../user.reducer";
import { UserBackendService } from "./user-backend.service";

export const TOKEN_KEY = "token.user.v1";

@Injectable()
export class UserAuthService implements OnDestroy {
	public isForgetMe = true;
	public token$: Observable<string | undefined>;

	private subscribtion?: Subscription;

	constructor(
		private readonly backendService: UserBackendService,
		private readonly store: Store<UserState>
	) {
		const selectUser = (appState: any): UserState => appState.user;
		const selectToken = createSelector(selectUser, (state: UserState): string | undefined => state?.token);
		this.token$ = this.store.pipe(select(selectToken));
		const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || undefined;
		if (token) {
			this.store.dispatch(UserActions.setToken(token));
		}
		// this.store.dispatch(UserActions.setToken(token));
	}

	public login$(user: string, password: string): Observable<AuthenticateResponse> {
		return this.backendService.authenticate.authenticate.post$(user, password).pipe(
			tap(res => this.saveTokenToSession(res.uuid)),
			tap(res => this.writeUserData(res)),
			catchError(err => {
				this.store.dispatch(UserActions.setToken(undefined));
				return throwError(err);
			})
		);
	}

	public oauth$(accessToken: string): Observable<AuthenticateResponse> {
		return this.backendService.authenticate.oauth.post$(accessToken).pipe(
			tap(res => this.saveTokenToSession(res.uuid)),
			tap(res => this.writeUserData(res)),
			catchError(err => {
				this.store.dispatch(UserActions.setToken(undefined));
				return throwError(err);
			})
		);
	}

	public loginAndRemember$(user: string, password: string): Observable<AuthenticateResponse> {
		this.isForgetMe = false;
		return this.login$(user, password).pipe(
			tap(res => this.pushToken(res.uuid))
		);
	}

	public authToken(publicKeyId: string): Observable<AuthenticateToken> {
		return this.backendService.authenticate.authToken.get$(publicKeyId);
	}

	public authByCertificate(data: LoginByCertificate): Observable<AuthenticateResponse> {
		this.store.dispatch(setPending(true));
		return this.backendService.authenticate.authByCertificate.post$(data).pipe(
			tap(() => this.store.dispatch(setPending(false))),
			tap(res => this.saveTokenToSession(res.uuid)),
			tap(res => this.writeUserData(res)),
			catchError(err => {
				this.store.dispatch(UserActions.setToken(undefined));
				return throwError(err);
			})
		);
	}

	public logout(): void {
		this.backendService.authenticate.logout.post$().subscribe(
			() => {
				this.pushToken(undefined);
				this.saveTokenToSession(undefined);
			},
			err => throwError(err));
	}

	public ngOnDestroy(): void {
		if (this.isForgetMe)
			this.logout();
		if (this.subscribtion)
			this.subscribtion.unsubscribe();
	}

	private pushToken(token?: string): void {
		if (token)
			localStorage.setItem(TOKEN_KEY, token);
		else
			localStorage.removeItem(TOKEN_KEY);
	}

	private saveTokenToSession(token?: string): void {
		if (token)
			sessionStorage.setItem(TOKEN_KEY, token);
		else
			sessionStorage.removeItem(TOKEN_KEY);
	}

	private writeUserData(res: { uuid: string, userRoles: Role[] }): void {
		this.store.dispatch(UserActions.setToken(res.uuid));
		this.store.dispatch(UserActions.setRoles(res.userRoles));
		if (!!window.sessionStorage?.getItem("token.user.v1")) {
			this.store.dispatch(UserActions.getProfile());
		}
	}
}
